/**
 * v2ServicesRepository — the ONLY interface page/component code uses.
 *
 * Phase 5 reads from the in-repo static catalog. Later, the internals swap
 * to `fetch('/api/public/services')` backed by MongoDB — the public
 * contract below stays identical, so pages will not need to change.
 *
 * All methods are ASYNC on purpose (even though local reads resolve
 * synchronously) so consumers already handle loading / success / not-found
 * / error states from day one.
 */
import V2_SERVICES from '../data/v2Services';

const SUPPORTED_LANGS = ['en', 'es'];

/**
 * Normalize a language input; unsupported values fall back to English.
 * @param {string|undefined} lang
 * @returns {'en'|'es'}
 */
export function normalizeLang(lang) {
  const l = typeof lang === 'string' ? lang.trim().toLowerCase() : '';
  return SUPPORTED_LANGS.includes(l) ? l : 'en';
}

/**
 * Localize a single service into a page-ready object.
 * Pages receive already-resolved strings/arrays for the active language.
 */
function localize(service, lang) {
  const L = normalizeLang(lang);
  const pick = (field) =>
    field && typeof field === 'object' ? (L in field ? field[L] : field.en) : field;
  const seo = service.seo || { title: {}, description: {} };
  return {
    id: service.id,
    slug: service.slug,
    status: service.status,
    order: service.order,
    featured: !!service.featured,
    kicker: pick(service.kicker) || '',
    title: pick(service.title),
    short_description: pick(service.short_description),
    overview: pick(service.overview),
    ideal_for: pick(service.ideal_for),
    problems_addressed: pick(service.problems_addressed) || [],
    deliverables: pick(service.deliverables) || [],
    outcomes: pick(service.outcomes) || [],
    pricing: pick(service.pricing),
    cta_label: pick(service.cta_label),
    related_service_ids: Array.isArray(service.related_service_ids)
      ? service.related_service_ids
      : [],
    seo: {
      title: pick(seo.title) || '',
      description: pick(seo.description) || '',
    },
  };
}

function byOrder(a, b) {
  return (a.order ?? 0) - (b.order ?? 0);
}

// Simulate a promise so the async contract is enforced in-memory
async function _resolve(value) {
  return Promise.resolve(value);
}

/**
 * Return every published service, localized, sorted by `order` ascending.
 * @returns {Promise<Array>}
 */
export async function getPublishedServices({ lang = 'en' } = {}) {
  const list = V2_SERVICES.filter((s) => s.status === 'published')
    .slice()
    .sort(byOrder)
    .map((s) => localize(s, lang));
  return _resolve(list);
}

/**
 * Featured subset of published services (used on the /v2 landing page later).
 * @returns {Promise<Array>}
 */
export async function getFeaturedServices({ lang = 'en', limit = 6 } = {}) {
  const list = V2_SERVICES.filter((s) => s.status === 'published' && s.featured)
    .slice()
    .sort(byOrder)
    .slice(0, Math.max(0, limit))
    .map((s) => localize(s, lang));
  return _resolve(list);
}

/**
 * Return a single service by slug or null.
 * @returns {Promise<Object|null>}
 */
export async function getServiceBySlug(slug, { lang = 'en' } = {}) {
  const s = V2_SERVICES.find(
    (x) => x.status === 'published' && x.slug === slug
  );
  return _resolve(s ? localize(s, lang) : null);
}

/**
 * Related services for a given slug.
 * Rule: use explicit related_service_ids first, then fill remaining slots
 * from other published services in `order` (excluding the service itself
 * and anything already returned). No category-based fallback.
 * @returns {Promise<Array>}
 */
export async function getRelatedServices(slug, { lang = 'en', limit = 3 } = {}) {
  const source = V2_SERVICES.find((x) => x.slug === slug);
  const published = V2_SERVICES.filter((x) => x.status === 'published');
  const byId = new Map(published.map((x) => [x.id, x]));
  const selected = [];
  const seen = new Set();
  if (source) seen.add(source.id);

  // 1. explicit related IDs (order preserved)
  if (source && Array.isArray(source.related_service_ids)) {
    for (const rid of source.related_service_ids) {
      if (selected.length >= limit) break;
      const cand = byId.get(rid);
      if (cand && !seen.has(cand.id)) {
        selected.push(cand);
        seen.add(cand.id);
      }
    }
  }

  // 2. fill remaining by display order
  if (selected.length < limit) {
    const remaining = published.slice().sort(byOrder);
    for (const cand of remaining) {
      if (selected.length >= limit) break;
      if (!seen.has(cand.id)) {
        selected.push(cand);
        seen.add(cand.id);
      }
    }
  }

  return _resolve(selected.map((s) => localize(s, lang)));
}

/**
 * DEV-ONLY catalog integrity check.
 * Verifies unique IDs, unique slugs, unique order values, and that every
 * related_service_id resolves to a known service.
 * Throws on failure to surface authoring mistakes early.
 * Safe to call at import-time in development; noop in production.
 */
export function _validateCatalog() {
  if (process.env.NODE_ENV === 'production') return { ok: true };
  const ids = new Set();
  const slugs = new Set();
  const orders = new Set();
  const knownIds = new Set(V2_SERVICES.map((s) => s.id));
  const errors = [];

  V2_SERVICES.forEach((s, idx) => {
    if (!s.id || ids.has(s.id)) errors.push(`Duplicate or missing id at index ${idx}: ${s.id}`);
    if (!s.slug || slugs.has(s.slug)) errors.push(`Duplicate or missing slug at index ${idx}: ${s.slug}`);
    if (s.order === undefined || orders.has(s.order))
      errors.push(`Duplicate or missing order at index ${idx}: ${s.order}`);
    ids.add(s.id);
    slugs.add(s.slug);
    orders.add(s.order);
    (s.related_service_ids || []).forEach((rid) => {
      if (!knownIds.has(rid))
        errors.push(`Unknown related_service_id "${rid}" on service "${s.id}"`);
    });
  });

  if (errors.length > 0) {
    // eslint-disable-next-line no-console
    console.error('[v2ServicesRepository] catalog validation errors:', errors);
    return { ok: false, errors };
  }
  return { ok: true };
}

// Run catalog validation at import-time in development
_validateCatalog();
